import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { NORMAL_PLAYBACK_RATE } from '@/presentation/util/playbackRate';
import { SKIP_SECONDS, skippedTime } from '@/presentation/util/mediaSkip';

const HAVE_METADATA = 1;

interface UseMediaPlayerOptions {
  src: string;
  initialResumePercent?: number;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
}

export interface MediaPlayer<T extends HTMLMediaElement> {
  mediaRef: RefObject<T>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  playbackRate: number;
  hasError: boolean;
  togglePlay: () => void;
  skipBackward: () => void;
  skipForward: () => void;
  seekToPercent: (percent: number) => void;
  changeVolume: (percent: number) => void;
  toggleMute: () => void;
  changePlaybackRate: (rate: number) => void;
}

export const useMediaPlayer = <T extends HTMLMediaElement>({
  src,
  initialResumePercent = 0,
  onProgress,
  onComplete,
}: UseMediaPlayerOptions): MediaPlayer<T> => {
  const mediaRef = useRef<T>(null);
  const hasResumedRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(NORMAL_PLAYBACK_RATE);
  const [hasError, setHasError] = useState(false);

  const handleTimeUpdate = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;
    setCurrentTime(media.currentTime);
    if (onProgress && media.duration) {
      onProgress((media.currentTime / media.duration) * 100);
    }
  }, [onProgress]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    onComplete?.();
  }, [onComplete]);

  const resumeIfNeeded = useCallback(
    (media: T) => {
      if (hasResumedRef.current || !media.duration) return;
      hasResumedRef.current = true;
      if (initialResumePercent <= 0) return;
      media.currentTime = (initialResumePercent / 100) * media.duration;
      setCurrentTime(media.currentTime);
    },
    [initialResumePercent],
  );

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    const handleLoadedMetadata = () => {
      setDuration(media.duration);
      setHasError(false);
      resumeIfNeeded(media);
    };
    const handleError = () => {
      setIsPlaying(false);
      setHasError(true);
    };

    media.addEventListener('timeupdate', handleTimeUpdate);
    media.addEventListener('loadedmetadata', handleLoadedMetadata);
    media.addEventListener('ended', handleEnded);
    media.addEventListener('error', handleError);

    if (media.readyState >= HAVE_METADATA) {
      handleLoadedMetadata();
    }

    return () => {
      media.removeEventListener('timeupdate', handleTimeUpdate);
      media.removeEventListener('loadedmetadata', handleLoadedMetadata);
      media.removeEventListener('ended', handleEnded);
      media.removeEventListener('error', handleError);
    };
  }, [handleTimeUpdate, handleEnded, resumeIfNeeded]);

  useEffect(() => {
    setHasError(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setPlaybackRate(NORMAL_PLAYBACK_RATE);
    hasResumedRef.current = false;
    const media = mediaRef.current;
    if (media) {
      media.playbackRate = NORMAL_PLAYBACK_RATE;
    }
  }, [src]);

  const togglePlay = () => {
    const media = mediaRef.current;
    if (!media) return;
    if (isPlaying) {
      media.pause();
    } else {
      media.play();
    }
    setIsPlaying(!isPlaying);
  };

  const seekToPercent = (percent: number) => {
    const media = mediaRef.current;
    if (!media) return;
    const newTime = (percent / 100) * duration;
    media.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skipSeconds = (seconds: number) => {
    const media = mediaRef.current;
    if (!media) return;
    media.currentTime = skippedTime(media.currentTime, media.duration, seconds);
    setCurrentTime(media.currentTime);
  };

  const skipBackward = () => skipSeconds(-SKIP_SECONDS);

  const skipForward = () => skipSeconds(SKIP_SECONDS);

  const changeVolume = (percent: number) => {
    const newVolume = percent / 100;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    const media = mediaRef.current;
    if (media) {
      media.volume = newVolume;
      media.muted = newVolume === 0;
    }
  };

  const toggleMute = () => {
    const media = mediaRef.current;
    if (!media) return;
    const muted = !isMuted;
    setIsMuted(muted);
    media.muted = muted;
  };

  const changePlaybackRate = (rate: number) => {
    setPlaybackRate(rate);
    const media = mediaRef.current;
    if (!media) return;
    media.preservesPitch = true;
    media.playbackRate = rate;
  };

  return {
    mediaRef,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playbackRate,
    hasError,
    togglePlay,
    skipBackward,
    skipForward,
    seekToPercent,
    changeVolume,
    toggleMute,
    changePlaybackRate,
  };
};
